import React from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import { LINKS, PHOTOS } from "@/lib/links";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={TEST_IDS.hero.section}
      className="relative min-h-[100vh] w-full overflow-hidden bg-nearblack"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={PHOTOS.heroVilla}
          onError={(e) => {
            e.currentTarget.src = PHOTOS.heroVillaFallback;
          }}
          alt=""
          data-testid={TEST_IDS.hero.image}
          className="w-full h-full object-cover"
          loading="eager"
        />
        {/* Cinematic gradient overlays */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,26,23,0.55) 0%, rgba(28,26,23,0.45) 35%, rgba(28,26,23,0.65) 75%, rgba(28,26,23,0.92) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 60%, rgba(28,26,23,0) 0%, rgba(28,26,23,0.45) 70%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 min-h-[100vh] flex flex-col">
        <div className="flex-1 flex items-center pt-32 md:pt-36 pb-16">
          <div className="max-w-4xl">
            <p
              className="eyebrow-light mb-8"
              data-testid="hero-eyebrow"
            >
              Liz Dirocco · Strategic Banking · Infinite Possibilities
            </p>

            <h1
              data-testid={TEST_IDS.hero.headline}
              className="font-serif text-paper leading-[1.02] text-balance"
              style={{ fontSize: "clamp(2.4rem, 6.5vw, 5.8rem)", fontWeight: 400 }}
            >
              Strategic Banking.
              <br />
              <em className="italic text-camellight" style={{ fontWeight: 400 }}>
                Infinite Possibilities.
              </em>
            </h1>

            <p
              data-testid={TEST_IDS.hero.sub}
              className="mt-10 max-w-xl text-paper/85 text-base md:text-lg leading-[1.7] font-light"
            >
              Most people learn how to make money. Few learn how to structure it.
              I help high-income earners, business owners, and families build
              financial systems that create greater control, liquidity, and
              long-term flexibility.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a
                href={LINKS.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TEST_IDS.hero.ctaPrimary}
                className="btn-camel inline-flex items-center justify-center gap-2 group"
              >
                Work with Liz
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#newsletter"
                data-testid={TEST_IDS.hero.ctaSecondary}
                className="btn-outline-light inline-flex items-center justify-center"
              >
                Join the Letter
              </a>
              <a
                href="/quiz"
                data-testid="hero-cta-assessment"
                className="btn-outline-light inline-flex items-center justify-center"
              >
                Take the Assessment
              </a>
            </div>
          </div>
        </div>

        {/* Bottom rail: identity + scroll cue */}
        <div className="pb-10 md:pb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-t border-paper/15 pt-8">
            <div
              data-testid={TEST_IDS.hero.identity}
              className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10.5px] tracking-[0.32em] uppercase text-paper/70"
            >
              <span>Financial Educator</span>
              <span className="text-camellight">·</span>
              <span>Systems Thinker</span>
              <span className="text-camellight">·</span>
              <span>Infinite Banking Practitioner</span>
            </div>
            <a
              href="#approach"
              className="inline-flex items-center gap-3 text-[10.5px] tracking-[0.32em] uppercase text-paper/70 hover:text-camellight transition-colors"
            >
              <span>Explore</span>
              <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
